'use client';

import { selectVariant, useActivateProduct, useSelectedVariant, type PdpVariant } from '@/lib/variant-store';
import { formatCentsClient } from '@/lib/format';

interface Props {
  productId: string;
  variants:  PdpVariant[];
}

// One pill per variant (2mL / 10mL / 30mL ...). Each shows its price so the
// shopper sees the whole run without changing selection. Sold-out variants are
// dimmed and disabled; a variant open for pre-order stays selectable and is
// tagged "Pre-order".
export function VariantSelector({ productId, variants }: Props) {
  useActivateProduct(productId);
  const { variantId } = useSelectedVariant(variants);

  return (
    <div className="mb-6">
      <div className="flex items-baseline justify-between mb-3">
        <p className="text-xs uppercase tracking-[0.14em] text-ink/65">Size</p>
        {variantId && (
          <p className="text-xs text-muted-strong">{variants.find((v) => v.id === variantId)?.title}</p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {variants.map((v) => {
          const isSelected = variantId === v.id;
          const pre = !v.inStock && !!v.preorder;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => v.canBuy && selectVariant(v.id)}
              disabled={!v.canBuy}
              aria-pressed={isSelected}
              aria-label={`${v.title}, ${formatCentsClient(v.priceCents)}${v.canBuy ? (pre ? ', pre-order' : '') : ', sold out'}`}
              className={[
                'min-w-[88px] px-4 py-3 text-left transition-colors',
                'border text-xs uppercase tracking-[0.1em]',
                isSelected ? 'border-ink bg-ink text-bone' : 'border-ink/25 text-ink hover:border-ink',
                !v.canBuy && 'opacity-40 cursor-not-allowed line-through',
              ].filter(Boolean).join(' ')}
            >
              <span className="block font-medium">{v.title}</span>
              <span className={['block font-numerals text-[12px] tracking-normal mt-0.5', isSelected ? 'text-bone/80' : 'text-muted-strong'].join(' ')}>
                {formatCentsClient(v.priceCents)}
              </span>
              {pre && (
                <span className={['block text-[9px] tracking-[0.1em] mt-0.5', isSelected ? 'text-bone/80' : 'text-ink/65'].join(' ')}>
                  Pre-order
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
